import type { ModelParams } from "../models";
import type { PromptConfig } from "../trace";

import { type ChatbotPrompts, chatbotPromptFolders } from "./chatbot";
import { type DocGenPrompts, docGenPromptFolders } from "./doc-gen";
import { type EditableArtifactPrompts, editableArtifactPromptFolders } from "./editable-artifact";
import { type MainframePrompts, mainframePromptFolders } from "./mainframe";
import { type OtherPrompts, otherPromptFolders } from "./other";
import { type ParsingPrompts, parsingPromptFolders } from "./parsing";
import type {
	CodeBasedTableAnalysisToolDefinition,
	DeepDiffToolDefinition,
	ExecutePythonToolDefinition,
	ExtractTableDataToolDefinition,
	GenerateArtifactToolDefinition,
	LinkupToolDefinition,
	MailboxToolDefinition,
	RetrieverToolDefinition,
} from "./tools";

export type * from "./toolInput";
export type * from "./tools";

export type PromptParams =
	| ChatbotPrompts
	| DocGenPrompts
	| EditableArtifactPrompts
	| MainframePrompts
	| ParsingPrompts
	| OtherPrompts;

export type PromptName = PromptParams["name"];

export const PROMPT_FOLDERS = {
	chatbot: "chatbot",
	docGen: "doc-gen",
	editableArtifact: "editable-artifact",
	mainframe: "mainframe",
	other: "other",
	parsing: "parsing",
	pptx: "pptx",
	study: "study",
} as const;

export type PromptFolder = (typeof PROMPT_FOLDERS)[keyof typeof PROMPT_FOLDERS];

const promptNameToFolder: Record<PromptName, PromptFolder> = {
	...chatbotPromptFolders,
	...docGenPromptFolders,
	...editableArtifactPromptFolders,
	...mainframePromptFolders,
	...otherPromptFolders,
	...parsingPromptFolders,
};

export function getPromptPath(name: PromptName): string {
	return `${promptNameToFolder[name]}/${name}`;
}

export interface Prompt {
	name: PromptName;
	variables?: Record<string, unknown> | undefined;
}

export interface ComputedPrompt {
	messages: ModelParams["messages"];
	promptConfig: PromptConfig;
}

export interface PromptOptions {
	isBetaUser?: boolean;
	isBleedingEdgeUser?: boolean;
	additionalSystemInstructions?: string;
}

export interface ToolDefinition {
	name: string;
	description: string;
	parameters: Array<{
		name: string;
		type: "string" | "array" | "number" | "boolean" | "enum" | "null";
		enums?: string[] | undefined;
		description: string;
		mandatory: boolean;
	}>;
}

export interface ToolCall {
	id: string;
	name: ToolName;
	input: Record<string, unknown>;
	// Gemini-only: opaque signature echoed back on the functionCall part in
	// follow-up requests. Other providers leave this undefined.
	thoughtSignature?: string | undefined;
}

export type ToolDefinitions =
	| LinkupToolDefinition
	| RetrieverToolDefinition
	| CodeBasedTableAnalysisToolDefinition
	| DeepDiffToolDefinition
	| ExecutePythonToolDefinition
	| ExtractTableDataToolDefinition
	| GenerateArtifactToolDefinition
	| MailboxToolDefinition;
export type ToolName = ToolDefinitions["name"];

export function getToolPath(name: ToolName): string {
	return `tools/${name}`;
}
